import { HTMLAttributes, MouseEventHandler, useId } from "react";
import { FormattedMessage } from "react-intl";
import classNames from "classnames/bind";
import { Amount } from "app/components/ui/Amount/Amount";
import { ButtonLink } from "app/components/ui/ButtonLink/ButtonLink";
import { BadgeItem } from "app/components/ui/badge/BadgeItem";
import { ArrowRightIcon } from "app/icons/navigation";
import { Image } from "app/components/ui/Image/Image";
import { LinkProps } from "app/components/ui/Link/Link";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardLinkOverlay,
	CardMedia,
	CardTitle,
} from "app/components/ui/Card/Card";
import styles from "./MerchCard.module.scss";

const cx = classNames.bind(styles);

const IMAGE_SIZES = "(min-width: 768px) 404px, 265px";

export type MerchCardPhoto = { url: string; thumbnail?: string };

export type MerchCardProps = {
	/** Page the card opens: the title is a link stretched over the whole card. */
	to: LinkProps["to"];
	/** Fired by a click on the card, before the navigation. */
	onClick?: MouseEventHandler<HTMLAnchorElement>;
	/** Sanitized HTML title, resolved by the caller. */
	title: string;
	/** Sanitized HTML subtitle, resolved by the caller. */
	subtitle?: string;
	photo?: MerchCardPhoto;
	/** Shown when greater than 0; otherwise the "see offers" CTA label is displayed instead. */
	price?: number;
	/** Shows the "price drop" badge over the image. */
	showPriceDrop?: boolean;
} & Omit<HTMLAttributes<HTMLElement>, "children" | "title" | "onClick">;

export const MerchCard = ({
	to,
	onClick,
	title,
	subtitle,
	photo,
	price,
	showPriceDrop = false,
	...rest
}: MerchCardProps) => {
	const titleId = useId();
	const subtitleId = useId();
	const priceId = useId();
	const showPrice = (price ?? 0) > 0;
	const descriptionIds = [subtitle && subtitleId, showPrice && priceId].filter(Boolean).join(" ");

	return (
		<Card asChild variant="elevated">
			<article aria-labelledby={titleId} {...rest}>
				<CardMedia className={cx("merch-card__media")}>
					{showPriceDrop && (
						<div className={cx("merch-card__badge")}>
							<BadgeItem type="priceDrop" />
						</div>
					)}

					{photo?.url && (
						<Image
							fill
							src={photo.url}
							alt=""
							width={232}
							height={220}
							sizes={IMAGE_SIZES}
							thumbnail={photo.thumbnail}
						/>
					)}
				</CardMedia>

				<CardContent className={cx("merch-card__content")}>
					<CardTitle className={cx("merch-card__title")} data-testid="merch-card-title">
						<CardLinkOverlay
							id={titleId}
							to={to}
							onClick={onClick}
							aria-describedby={descriptionIds || undefined}
						>
							<span dangerouslySetInnerHTML={{ __html: title }} />
						</CardLinkOverlay>
					</CardTitle>

					{subtitle && (
						<CardDescription
							id={subtitleId}
							className={cx("merch-card__intro")}
							data-testid="merch-card-subtitle"
						>
							<span dangerouslySetInnerHTML={{ __html: subtitle }} />
						</CardDescription>
					)}
				</CardContent>

				<CardFooter className={cx("merch-card__footer")}>
					{showPrice && (
						<Amount
							id={priceId}
							value={price ?? 0}
							from
							perPerson
							data-testid="amount"
						/>
					)}

					<ButtonLink
						to={to}
						onClick={onClick}
						tabIndex={-1}
						aria-hidden="true"
						variant="primary"
						size={showPrice ? "icon-sm" : "sm"}
						fullWidth={!showPrice}
						endIcon={<ArrowRightIcon />}
						className={cx("merch-card__cta")}
					>
						{!showPrice && <FormattedMessage id="current.merch.collection.cta.label" />}
					</ButtonLink>
				</CardFooter>
			</article>
		</Card>
	);
};
